import { useState, useEffect, useRef, useCallback, useMemo } from 'react'

import 'chart.js/auto';
import { Chart, } from 'react-chartjs-2';

import { Checkbox, Menu, Typography, Tooltip as MuiTooltip, Button, TextField,  Switch, DialogActions, 
        Dialog, DialogTitle, DialogContent, Tooltip, CircularProgress, IconButton, Stack,
        Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel, TablePagination 
      } from '@mui/material'
import { DangerousOutlined, CheckCircleOutline, DateRangeOutlined,  TableView, Sync, Build,
         TableChartOutlined, Refresh, Visibility, Edit, PendingActions, SyncDisabledOutlined,
      } from '@mui/icons-material'

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import PieChartIcon from '@mui/icons-material/PieChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import BarChartIcon from '@mui/icons-material/BarChart';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close'
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';

import _ from 'underscore'
import _lodash from 'lodash'
import moment from 'moment'

import { C, D, getColorsByClass, exportStandardExcel, openLink } from '../include'
import { MediatecaQualificators } from './Mediateca.jsx'

moment.locale('pt_br')
// ChartJS.register(ArcElement, Tooltip, Legend)

// Plugin para forçar fundo branco na cópia/exportação de imagem
const whiteBackgroundPlugin = {
  id: 'customCanvasBackgroundColor',
  beforeDraw: (chart) => {
    const { ctx } = chart;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  }
};


// ====================================================
// Componente para apresentar os contadores no topo da janela
// ====================================================
const DashboardCard = ({ title, color, icon, value }) => (
  <MuiTooltip title={title} arrow>
    <div className="col e aF"
      style={{
        margin: '1px',
        backgroundColor: '#eeeeee',
        borderRadius: '0.3rem',
        padding: '0.3rem 0.5rem'
      }}
    >
      <div className="row aC" style={{ gap: '0.5rem', display: 'flex', alignItems: 'center' }}>
        <div className="row aC" style={{ backgroundColor: color, display: 'flex', padding: '4px', borderRadius: '4px' }}>
          {icon}
        </div>
        <Typography variant="body2" component="div" align="left" color="text.secondary">
          {value}
        </Typography>
      </div>
    </div>
  </MuiTooltip>
);


// ====================================================
// Componente que renderiza um grafico na area de graficos
// ====================================================

const DashboardGraph = ({ widthContainer = "100%", title, chartType, data, originalLabels, onElementClick, aspectRatio = '21 / 9' }) => {
  const chartRef = useRef(null);
  
  // Estado local para guardar o nome das barras que o usuário clicou para ocultar
  const [hiddenBars, setHiddenBars] = useState([]);
  
  // Guarda a referência da prop "data" anterior para comparação
  const [prevData, setPrevData] = useState(data);

  // Atualiza estado durante o render se a prop mudou
  if (data !== prevData) {
    setPrevData(data);
    setHiddenBars([]); // Reseta imediatamente, cortando a cascata pela raiz
  }

  // === PREPARAÇÃO DOS DADOS (Eixo X Dinâmico) ===
  let displayData = data;
  
  if (chartType === 'bar' && data?.labels && data?.datasets?.length > 0) {
    const dataset = data.datasets[0];
    const bgColorSource = dataset.backgroundColor || '#1e1efc'; // Cor padrão caso venha vazio
    
    const bgColors = Array.isArray(bgColorSource) 
      ? bgColorSource 
      : data.labels.map(() => bgColorSource);

    const filteredLabels = [];
    const filteredValues = [];
    const filteredColors = [];

    data.labels.forEach((label, idx) => {
      if (!hiddenBars.includes(label)) {
        filteredLabels.push(label);
        filteredValues.push(dataset.data ? dataset.data[idx] : 0);
        filteredColors.push(bgColors[idx] || bgColorSource);
      }
    });

    displayData = {
      labels: filteredLabels,
      datasets: [{
        ...dataset,
        data: filteredValues,
        backgroundColor: filteredColors
      }]
    };
  }

  // === EVENTO DE CLIQUE NA BARRA (Ajustado para Arrays Filtrados) ===
  const handleNativeClick = (event, elements) => {
    if (!elements || elements.length === 0) return;

    const datasetIndex = elements[0].datasetIndex;
    const dataIndex = elements[0].index;
    
    let realIndex = dataIndex;

    // Se o array foi filtrado (modo barra), temos que achar o index original para mandar pro Modal
    if (chartType === 'bar') {
      const clickedLabel = displayData.labels[dataIndex];
      realIndex = data.labels.indexOf(clickedLabel);
    }

    if (realIndex === -1) return;
    const valueClicked = data.datasets[datasetIndex].data[realIndex];

    if (onElementClick) {
      const rawLabel = originalLabels && originalLabels.length > realIndex 
        ? originalLabels[realIndex] 
        : String(data.labels[realIndex]).split(' = ')[0];

      onElementClick({ title, label: rawLabel, value: valueClicked });
    }
  };

  // === CONSTRUÇÃO CONDICIONAL DAS OPÇÕES DA LEGENDA ===
  const getChartOptions = () => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: 0 },
      animation: { animateRotate: true, animateScale: chartType === 'pie' },
      onClick: handleNativeClick,
      onHover: (event, elements) => {
        if (event.native && event.native.target) {
          event.native.target.style.cursor = elements && elements.length > 0 ? 'pointer' : 'default';
        }
      },
      plugins: {}
    };

    if (chartType === 'bar') {
      // Verifica se o gráfico ficou totalmente sem dados (por filtro ou ocultação manual)
      const isEmpty = !displayData?.labels?.length || !displayData?.datasets?.[0]?.data?.length;

      baseOptions.scales = {
        x: {
          display: !isEmpty, // Se estiver vazio, desliga o Eixo X inteiro
          ticks: { display: false }, // Esconde os nomes debaixo da barra
          grid: { display: false }
        },
        y: {
          display: !isEmpty // Se estiver vazio, desliga o Eixo Y inteiro
        }
      };

      // Configuração Customizada para Barra (Controlada pelo React State)
      baseOptions.plugins.legend = {
        display: true, position: 'bottom', align: 'center',
        labels: {
          boxWidth: 30, padding: 12, font: { size: 11 },
          generateLabels: () => {
            // Trava de segurança extra
            if (!data?.labels || !data?.datasets?.length) return [];

            const dataset = data.datasets[0];
            const bgColorSource = dataset.backgroundColor || '#1e1efc';
            
            const bgColors = Array.isArray(bgColorSource) 
              ? bgColorSource 
              : data.labels.map(() => bgColorSource);

            return data.labels.map((label, i) => ({
              text: label,
              fillStyle: bgColors[i] || bgColorSource, 
              strokeStyle: bgColors[i] || bgColorSource,
              hidden: hiddenBars.includes(label), 
              datasetIndex: 0
            }));
          }
        },
        onClick: (e, legendItem) => {
          const clickedLabel = legendItem.text;
          // Adiciona ou remove a barra do array de ocultos
          setHiddenBars(prev => prev.includes(clickedLabel) 
            ? prev.filter(item => item !== clickedLabel) 
            : [...prev, clickedLabel]
          );
        }
      };
    } else {
      // Configuração Nativa para Pizza/Doughnut (Sem interferir nas animações radiais)
      baseOptions.plugins.legend = {
        display: true, position: 'bottom', align: 'center',
        labels: { boxWidth: 30, padding: 12, font: { size: 11 } }
      };
    }

    return baseOptions;
  };

  return (
    <div style={{ 
      width: widthContainer, minWidth: '100%', margin: '0 0 10px 0', padding: '0.0rem 1rem', 
      backgroundColor: '#fff', borderRadius: '1rem', boxSizing: 'border-box', display: 'block', overflow: 'hidden' 
    }}>
      <div style={{ width: '100%', height: 'auto', maxHeight: 'calc(100vh - 270px)', aspectRatio: aspectRatio, position: 'relative' }}>
        <Chart 
          ref={chartRef}
          type={chartType} 
          data={displayData} 
          plugins={[ whiteBackgroundPlugin ]} 
          options={getChartOptions()}
        />
      </div>
    </div>
  );
};



// ====================================================
// === FUNÇOES AUXILIARES PARA DASHBOARD ===
// ====================================================

// mapa para os contadores
const STATUS_MAP = {
  'processado': 'proc',
  'não processado': 'nproc',
  'em processamento': 'onproc',
  'erro': 'procerr'
}

// Dicionário central de títulos dos gráficos
const DASH_TITLES = {
  TIPO: "Documentos por Tipo",
  FORNECEDOR: "Documentos por Fornecedor",
  REGIAO: "Documentos por " + C.groupLabel,
  EMBARCACAO: "Documentos por Embarcação",
  CERTIFICADOS: "Certificados de Calibração"
};


// Filter Engine
function filterDocs(docs = [], filters = {}) {
  const {
    showUnread = false,
    showPend = false,
    showEdt = false,
    selectedDocs = [],
    selectedFornec = [],
    selectedEmb = [],
    selectedReg = []
  } = filters;

  return docs.filter(item => {
    // filtro global: não lidos
    if (showUnread && item.rd_ctd !== 0) return false;

    // filtro global: com pendência
    if (showPend && item.temPendencia !== true) return false;

        // filtro global: editados
    if (showEdt && item.editado !== true) return false;

    // BLOQUEIO TOTAL SE USUÁRIO ZEROU A SELEÇÃO
    if (selectedDocs.length === 0) return false;
    if (selectedFornec.length === 0) return false;
    if (selectedEmb.length === 0) return false;
    if (selectedReg.length === 0) return false;

    // filtros dimensionais
    if (!selectedDocs.includes(item.doctype)) return false;
    if (!selectedFornec.includes(item.supplier)) return false;
    if (!selectedEmb.includes(item.boat)) return false;
    if (!selectedReg.includes(item.region)) return false;

    return true;
  });
}

// funcao de apoio para envelopar o 'mais info'
const QualificatorsViewer = ({ docId, api }) => {
  const [ocs, setOcs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true; // Previne vazamento de memória se o usuário fechar a modal rápido
    
    api.get("/mediateca/ocorrencias/" + docId)
      .then((r) => {
        if (isMounted) {
          setOcs(r);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Erro ao buscar ocorrências:", err);
        if (isMounted) setLoading(false);
      });

    return () => { isMounted = false; };
  }, [docId, api]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
        <CircularProgress />
      </div>
    );
  }

  return <MediatecaQualificators key={docId} ocs={ocs} />;
};

// ====================================================
// COMPONENTE para modal analitico dos graficos
// ====================================================
const AnalyticModal = ({ open, onClose, title, label, items = [], filterText, api }) => {

/* STRUCT ITEMS:
      aprov: "NÃO TRATADO"
      boat: "Hercules"
      doc_id: 2110
      doctype: "Thermography"
      dtaprox: "09/03/2017"
      editado: false ou true
      pr_om: false ou true
      rd_ctd: 1 ou 0
      region: "Rio de Janeiro-RJ"
      status: "processado"
      supplier: "Schottel"
      temPendencia: true ou false
*/

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // --- ESTADOS PARA ORDENAÇÃO E BUSCA ---
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('doc_id');   // coluna inicial do sort
  const [searchQuery, setSearchQuery] = useState('');
  
  // Estado que controla o documento selecionado para abrir a sub-modal de ocorrências
  const [selectedRowContext, setSelectedRowContext] = useState(null);

  // Aplica a Busca Rápida
  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;

    const lowerQ = searchQuery.toLowerCase();
    return items.filter(row => 
      String(row.doc_id).toLowerCase().includes(lowerQ) ||
      String(row.doctype).toLowerCase().includes(lowerQ) ||
      String(row.supplier).toLowerCase().includes(lowerQ) ||
      String(row.boat).toLowerCase().includes(lowerQ) ||
      String(row.region).toLowerCase().includes(lowerQ)
    );
  }, [items, searchQuery]);

  // Aplica a Ordenação na lista filtrada
  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      let valA = a[orderBy] || '';
      let valB = b[orderBy] || '';

      // Se o campo for a data, aplicamos a inversão (de DD/MM/AAAA para AAAAMMDD)
      if (orderBy === 'dtaprox') {
        const inverteData = (str) => {
          if (!str || typeof str !== 'string') return '';
          const [day, month, year] = str.trim().split('/');
          return `${year}${month}${day}`;
        };

        valA = inverteData(valA);
        valB = inverteData(valB);
      } else {
        // Comportamento padrão para os outros campos
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();
      }

      // ordenacao final
      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredItems, order, orderBy]);


  // calcula maior pagina possivel para o set
  // a paginação agora lê do 'sortedItems' e não mais do 'items' puro
  const maxPage = Math.max(0, Math.ceil(sortedItems.length / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);
  const paginatedItems = sortedItems.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  // Função que inverte a ordem quando clica na mesma coluna
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // OnClick de cada linha do analitico
  const handleOnRowClick = (row) => { setSelectedRowContext(row); };

  // handlers de paginação
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Função para exportar a lista analítica para o Excel
  const exportAnalyticToExcel = () => {
    if (!sortedItems || sortedItems.length === 0) return;

    // 1. Preparação dos Filtros (Combinando global + busca local)
    const partesFiltro = [];
    if (filterText) partesFiltro.push(filterText.replace(/Filtros: /g, ''));
    if (searchQuery) partesFiltro.push(`Busca: "${searchQuery}"`);
    const stringFiltros = partesFiltro.length > 0 ? partesFiltro.join(', ') : '';

    const data = sortedItems.map(row => {      
      // Constrói o status especificamente para a linha atual (row)
      const partesStatus = [];
      
      if (row.status === "processado") partesStatus.push("✅");
      else if (row.status === "em processamento") partesStatus.push("🔃");
      else if (row.status === "não processado") partesStatus.push("⏳");
      else if (row.status === "erro") partesStatus.push("❌");
      
      if (row.rd_ctd === 0) partesStatus.push("✉️");
      if (row.temPendencia) partesStatus.push("⚠️");
      if (row.pr_om) partesStatus.push("🔧");
      if (row.editado) partesStatus.push("✏️");
      
      // Retorna o array correspondente a esta linha
      return [
        row.doc_id,
        partesStatus.join(" "),
        row.doctype,
        row.dtaprox ? row.dtaprox : 'Indeterminada',
        row.supplier ? row.supplier : 'Indeterminado',
        row.boat ? row.boat : 'Indeterminada',
        row.region ? row.region : 'Indeterminado'
      ];
    });

    // Chamada da Função Genérica
    exportStandardExcel({
      title: `Analítico: ${title}: ${label} (${sortedItems.length.toLocaleString('pt-BR')})`,
      filtersText: stringFiltros,
      columns: [
        { header: 'ID', width: 7 },
        { header: 'Status', width: 15 },
        { header: 'Tipo de Documento', width: 25 },
        { header: 'Data Aproximada', width: 15 },
        { header: 'Fornecedor', width: 20 },
        { header: 'Embarcação', width: 20 },
        { header: 'Filial-Grupo', width: 20 }
      ],
      data: data,
      fileNamePrefix: `Analitico_${title}`
    });
  };

  return (
    <>
      {/* NÍVEL 1: Modal Principal da Tabela Analítica */}
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ lineHeight: 1.2 }}>
                {title}: <b>{label}</b>
                {searchQuery 
                  ? ` (${sortedItems.length.toLocaleString('pt-BR')} de ${items.length.toLocaleString('pt-BR')} documentos)` 
                  : ` (${items.length.toLocaleString('pt-BR')} documentos)`
                }
              </span>
              {filterText && (
                <span style={{ fontSize: '1rem', color: '#666', marginTop: '4px', fontWeight: 'normal' }}>
                  {filterText}
                </span>
              )}
            </div>
            
            {/* AGRUPAMENTO DOS BOTÕES DE AÇÃO */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPage(0);
                }}
                sx={{ backgroundColor: '#fff', borderRadius: '4px', width: '200px' }}
              />

              <Tooltip title="Exportar para Excel" arrow>
                <IconButton 
                  size="large" 
                  sx={{ marginLeft: '0.3rem', color: '#106436ff' }} 
                  onClick={exportAnalyticToExcel}
                  disabled={sortedItems.length === 0} // Previne clique se não houver dados
                >
                  <TableChartOutlined />
                </IconButton>
              </Tooltip>

              <Tooltip title="Fechar" arrow>
                <IconButton size="large" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>

          </div>
        </DialogTitle>

        <DialogContent dividers style={{ padding: 0 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '4ch', maxWidth: '4ch' }}>
                  <TableSortLabel active={orderBy === 'doc_id'} direction={orderBy === 'doc_id' ? order : 'asc'} onClick={() => handleRequestSort('doc_id')}>
                    <b>ID</b>
                  </TableSortLabel>
                </TableCell>
                
                <TableCell align="left" style={{ width: 'calc(11ch + 40px)', maxWidth: 'calc(11ch + 40px)' }}>
                  {/* <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleRequestSort('status')}> */}
                    <b>Status</b>
                  {/* </TableSortLabel> */}
                </TableCell>
                
                <TableCell>
                  <TableSortLabel active={orderBy === 'doctype'} direction={orderBy === 'doctype' ? order : 'asc'} onClick={() => handleRequestSort('doctype')}>
                    <b>Tipo de Documento</b>
                  </TableSortLabel>
                </TableCell>
                
                <TableCell>
                  <TableSortLabel active={orderBy === 'dtaprox'} direction={orderBy === 'dtaprox' ? order : 'asc'} onClick={() => handleRequestSort('dtaprox')}>
                    <b>Data Aproximada</b>
                  </TableSortLabel>
                </TableCell>
                
                <TableCell>
                  <TableSortLabel active={orderBy === 'supplier'} direction={orderBy === 'supplier' ? order : 'asc'} onClick={() => handleRequestSort('supplier')}>
                    <b>Fornecedor</b>
                  </TableSortLabel>
                </TableCell>
                
                <TableCell>
                  <TableSortLabel active={orderBy === 'boat'} direction={orderBy === 'boat' ? order : 'asc'} onClick={() => handleRequestSort('boat')}>
                    <b>Embarcação</b>
                  </TableSortLabel>
                </TableCell>
                
                <TableCell>
                  <TableSortLabel active={orderBy === 'reg'} direction={orderBy === 'region' ? order : 'asc'} onClick={() => handleRequestSort('region')}>
                    <b>Filial-Grupo</b>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedItems.map((row, index) => (
                <Tooltip 
                  key={row.doc_id ?? `row-${index}`} 
                  title="Mais Info" 
                  arrow 
                  placement="top" 
                  followCursor
                >
                  <TableRow 
                    hover
                    onClick={() => handleOnRowClick(row)} 
                    sx={{
                      cursor: 'pointer',
                      '& .MuiTableCell-root': { fontWeight: row.rd_ctd == 0 ? 'bold' : 'normal' }
                     }}
                  >
                    <TableCell>{row.doc_id}</TableCell>

                    {/* ÍCONES DE STATUS */}
                    <TableCell align="left">
                      {row.status === "processado" && (
                        <CheckCircleOutline sx={{ color: row.rd_ctd === "0" ? "success.dark" : "success.main" }} />
                      )}
                      {row.status === "em processamento" && ( <Sync color="warning" /> )}
                      {row.status === "não processado" && ( <SyncDisabledOutlined color="action" /> )}
                      {row.status === "erro" && ( <DangerousOutlined color="error" /> )}
                      
                      {row.temPendencia && ( <PendingActions color="warning" style={{ fontSize: 20 }}/> )}
                      {row.pr_om && (<Build color="warning" style={{ fontSize: 20, transform: 'scaleX(-1)' }}/> )}
                      {row.editado && ( <Edit color="warning" style={{ fontSize: 22 }}/> )}
                    </TableCell>

                    <TableCell>{row.doctype}</TableCell>
                    <TableCell>{row.dtaprox ? row.dtaprox : 'Indeterminada'}</TableCell>
                    <TableCell>{row.supplier ? row.supplier: 'Indeterminado'}</TableCell>
                    <TableCell>{row.boat ? row.boat: 'Indeterminada'}</TableCell>
                    <TableCell>{row.region ? row.region: 'Indeterminado'}</TableCell>
                  </TableRow>
                </Tooltip>
              ))}
              
              {/* SEMPRE DEVE HAVER ITENS PQ O ANALITICO É ACIONADO PELO GRAFICO              
              {sortedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: '2rem' }}>
                    Nenhum documento encontrado para este filtro.
                  </TableCell>
                </TableRow>
              )} */}

            </TableBody>
          </Table>
        </DialogContent>

        <TablePagination
          style={{ overflow: 'hidden', flexShrink: 0 }}
          sx={{
            userSelect: 'none',
            '& .MuiTablePagination-select': {
              border: '1px solid #c4c4c4',
              borderRadius: '4px',
              paddingTop: '2px',
              paddingBottom: '2px',
            }
          }}
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={sortedItems.length}
          rowsPerPage={rowsPerPage}
          page={safePage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Documentos/página:"
          labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString('pt-BR')}–${to.toLocaleString('pt-BR')} de ${count !== -1 ? count.toLocaleString('pt-BR') : `mais de ${to.toLocaleString('pt-BR')}`}`}
          showFirstButton
          showLastButton
        />
      </Dialog>

      {/* NÍVEL 2: Sub-Modal que renderiza as ocorrências do documento (Qualificators) */}
      <Dialog 
        open={!!selectedRowContext} 
        onClose={() => setSelectedRowContext(null)} 
        maxWidth="xs" // largura do modal
        fullWidth
      >
        <DialogTitle sx={{ padding: '6px 12px' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ lineHeight: 0.8 }}>
              Ocorrências do Documento {selectedRowContext?.doc_id}
            </span>
            <Tooltip title="Abrir painel de leitura" arrow>
              <Visibility 
                height="48px" 
                className="hv" 
                color="action" 
                style={{ cursor: 'pointer' }}
                onClick={() => openLink(`/viewer.html?p=${selectedRowContext?.doc_id}`)} 
              />
            </Tooltip>

            <Tooltip title="Fechar" arrow>
              <IconButton size="large" onClick={() => setSelectedRowContext(null)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>
        </DialogTitle>
        <DialogContent dividers style={{ backgroundColor: '#f9f9f9', minHeight: '250px', padding: '16px' }}>
          {selectedRowContext && (
            <QualificatorsViewer 
              docId={selectedRowContext.doc_id} 
              api={api} 
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};


// =====================================
// === CLASSE PRINCIPAL DO DASHBOARD ===
// =====================================

export const DashboardSA = ({ api, user }) => {
  const hasMounted = useRef(false);
  const isReady = useRef(false);
  const saveTimerRef = useRef(null);  // timer para salvamento das prefs

  // === STATE UNIFICADO ===
  const [state, _setState] = useState({
    // recordsets globais
    rawdcs: [], rawCerts: [],   // DOCS E CERTIFICADOS
    
    // recordsets por dimensao
    docs: { chart: { labels: [], datasets: [] }, table: { labels: [], values: [] } },
    suppliers: { chart: { labels: [], datasets: [] }, table: { labels: [], values: [] } },
    boats: { chart: { labels: [], datasets: [] }, table: { labels: [], values: [] } },
    region: { chart: { labels: [], datasets: [] }, table: { labels: [], values: [] } },
    certs: { chart: { labels: [], datasets: [] }, table: { labels: [], values: [] } },
    // micro-otimização estrutural dos contadores de documentos por dimensao
    aggDocs: {}, aggSuppliers: {}, aggBoats: {}, aggReg: {}, aggCerts: {},
    
    // listas de itens dos menus de selecao de cada dimensao
    tiposDeDocs: [], tiposDeFornec: [], tiposDeEmb: [], tiposDeReg: [], tiposDeCert: [],
    selectedDocs: [], selectedFornec: [], selectedEmb: [], selectedCC: [], selectedReg: [],
    selectedAll: {
      selectedDocs: true, selectedFornec: true, selectedEmb: true, selectedReg: true, selectedCC: true
    },

    // contadores
    stats: { proc: 0, procerr: 0, unread: 0, pend: 0, edt: 0, }, //nproc: 0, onproc: 0, total: 0, 

    // controles dos filtros
    dataini: null, datafim: null, aproxIniDateMenu: null, aproxFimDateMenu: null,
    showUnread: false, showPend: false, showEdt: false, 
    savedMenuFilters: null, // prefs dos menus
    
    // flags de controle da renderizacao dos modais de tabelas (sumárias e analiticas)
    showDocsTable: false, showSuppliersTable: false, showRegTable: false, showBoatsTable: false, showCertsTable: false,
    showAnalyticModal: false, // controle do modal da tabela do sintetico do grafico
    analyticData: { title: '', label: '', items: [] }, // Guarda os detalhes do sintetico do grafico

    // controle dos graficos
    chartType: 'bar', sortType: 'num', sortOrd: 'desc', loadingDash: false,
    windowWidth: window.innerWidth,
  });

  const stateRef = useRef(state);
  const prevDeps = useRef({});

  const updateState = useCallback((updater) => {
    let nextState;
    if (typeof updater === "function") {
      nextState = { ...stateRef.current, ...updater(stateRef.current) };
    } else {
      nextState = { ...stateRef.current, ...updater };
    }
    stateRef.current = nextState;
    _setState(nextState);
    return nextState;
  }, []);


  // FUNÇÃO CENTRALIZADA DE SALVAMENTO DAS PREFS
  const saveUserPreferences = async (currentState) => {
    const dataIniFormat = currentState.dataini ? moment(currentState.dataini).format('DD/MM/YYYY') : '';
    const dataFimFormat = currentState.datafim ? moment(currentState.datafim).format('DD/MM/YYYY') : '';

    // preferencias do usuario
    const fullPreferences = {
      user_id: user.id,
      
      // Chaves que o Dashboard governa integralmente
      graphdata: { 
        chartType: currentState.chartType, 
        sortType: currentState.sortType, 
        sortOrd: currentState.sortOrd 
      },
      
      // Chaves compartilhadas (garantimos a formatação esperada pelas outras telas)
      selectDateParameters: { dataInicio: dataIniFormat, dataFim: dataFimFormat },
      unread: Boolean(currentState.showUnread),
      pendencias: currentState.showPend ? 1 : 0, 
      edt: currentState.showEdt ? 1 : 0,

      // seleçoes dos menus dos graficos
      selectedDash: {
        selectedDocs: currentState.selectedDocs,
        selectedFornec: currentState.selectedFornec,
        selectedEmb: currentState.selectedEmb,
        selectedReg: currentState.selectedReg,
        selectedCC: currentState.selectedCC
      }
    };

    await api.post("/user/preferences", fullPreferences).catch(console.error);
  };


  // =========================================================================
  // MÉTODOS CORE
  // =========================================================================

  const sortKeyValueMap = (obj) => {
    const s = stateRef.current;
    const entries = Object.entries(obj);
    entries.sort((a, b) => {
      if (s.sortType === 'num') return s.sortOrd === 'asc' ? a[1] - b[1] : b[1] - a[1];
      const cmp = a[0].localeCompare(b[0], 'pt', { sensitivity: 'base', ignorePunctuation: true });
      return s.sortOrd === 'asc' ? cmp : -cmp;
    });
    return Object.fromEntries(entries);
  };

  // calcula os conteudos do recordset indicado 
  const getDataSet = (dt) => {
    if (!dt) return { chart: { labels: [], datasets: [{ data: [0] }] }, table: { labels: [], values: [] } };

// eslint-disable-next-line no-unused-vars
    const { 'Sem pendência': _, ...rest } = dt;
    const sorted = sortKeyValueMap(rest);
    const keys = Object.keys(sorted);
    const values = Object.values(sorted);
    const chartLabels = keys.map((item, idx) => `${item} = ${new Intl.NumberFormat('pt-BR').format(values[idx])}`);
    return {
      chart: {
        labels: chartLabels,
        datasets: [{ data: values, backgroundColor: keys.length > 1 ? getColorsByClass(keys.length) : ['#1e1efc'], borderWidth: 0 }]
      },
      table: { labels: keys, values }
    };
  };

  // calcula os contadores totais
  const getStats = (dcs = []) => {
    // Apenas os contadores realmente usados
    const stats = { proc: 0, procerr: 0, unread: 0, pend: 0, edt: 0 };
    const len = dcs.length;

    for (let i = 0; i < len; i++) {
      const { status, rd_ctd, temPendencia, editado } = dcs[i];
      const key = STATUS_MAP[status];
      
      if (key) stats[key]++;

      // Só avalia leitura, pendência e edição se o doc estiver PROCESSADO
      const isProcessado = String(status).trim().toLowerCase() === 'processado';
      if (isProcessado) {
        if (rd_ctd === 0) stats.unread++;
        if (temPendencia) stats.pend++;
        if (editado) stats.edt++;
      }
    }
    return stats;
  };

  // recalcula os filtros de todos os dashboards
  const updateDashAll = (fd, fcc, selectedCertLabels) => {
    const s = stateRef.current;

    // Processa os dados dos 4 gráficos principais (usando fd)
    const dc = _.mapObject(_.groupBy(fd, 'doctype'), v => v.length);
    const sp = _.mapObject(_.groupBy(fd, 'supplier'), v => v.length);
    const bt = _.mapObject(_.groupBy(fd, 'boat'), v => v.length);
    const reg = _.mapObject(_.groupBy(fd, 'region'), v => v.length);

    // Processa os dados exclusivos do gráfico de certificados (usando fcc)
    const cc = fcc.filter(item => selectedCertLabels.includes(item.label));
    const dCC = _.mapObject(_.groupBy(cc, 'label'), v => v.length);

    // Dispara um ÚNICO update de estado com tudo junto
    updateState({
      stats: getStats(s.rawdcs), // calcula Contadores do topo
      
      aggDocs: dc, 
      aggSuppliers: sp, 
      aggBoats: bt, 
      aggReg: reg,
      aggCerts: dCC,

      // Monta os dados finais para os componentes gráficos
      docs: getDataSet(dc), 
      suppliers: getDataSet(sp), 
      boats: getDataSet(bt), 
      region: getDataSet(reg),
      certs: getDataSet(dCC)
    });
  };

  // aplica os filtros sobre os recordsets
  const applyFilter = (o = {}) => {
    const s = stateRef.current;
    // prepara o pacote de filtros (fts)
    const fts = {
      dataini: ('dataini' in o) ? o.dataini : s.dataini,
      datafim: ('datafim' in o) ? o.datafim : s.datafim,
      selectedDocs: o.selectedDocs ?? s.selectedDocs,
      selectedFornec: o.selectedFornec ?? s.selectedFornec,
      selectedEmb: o.selectedEmb ?? s.selectedEmb,
      selectedReg: o.selectedReg ?? s.selectedReg,
      selectedCC: o.selectedCC ?? s.selectedCC,
    };
    
    // aplica os filtros no conjunto completo
    const raw = s.rawdcs || [];
    // Garante que os gráficos usem APENAS documentos processados
    const rawProcessados = raw.filter(d => String(d.status).trim().toLowerCase() === 'processado');
 
    const fd = filterDocs(rawProcessados, {
      showUnread: s.showUnread, showPend: s.showPend, showEdt: s.showEdt,
      selectedDocs: fts.selectedDocs, selectedFornec: fts.selectedFornec,
      selectedEmb: fts.selectedEmb, selectedReg: fts.selectedReg
    });

    // aplica os filtros no conjunto dos certificados
    const rawcc = s.rawCerts || [];
    const rawccProcessados = rawcc.filter(d => String(d.status).trim().toLowerCase() === 'processado');

    const fcc = filterDocs(rawccProcessados, {
      showUnread: s.showUnread, showPend: s.showPend, showEdt: s.showEdt,
      selectedDocs: fts.selectedDocs, selectedFornec: fts.selectedFornec,
      selectedEmb: fts.selectedEmb, selectedReg: fts.selectedReg
    });

    // Update state and trigger re-renders natively inside hook
    updateState({
      ...fts,
      selectedAll: {
        selectedDocs: fts.selectedDocs.length === s.tiposDeDocs.length,
        selectedFornec: fts.selectedFornec.length === s.tiposDeFornec.length,
        selectedEmb: fts.selectedEmb.length === s.tiposDeEmb.length,
        selectedReg: fts.selectedReg.length === s.tiposDeReg.length,
        selectedCC: fts.selectedCC.length === s.tiposDeCert.length,
      }
    });

    updateDashAll(fd, fcc, fts.selectedCC);
  };


  const cleanFilter = () => {
    const s = stateRef.current;
    updateState({ showUnread: false, showPend: false, showEdt: false, });
    applyFilter({
      dataini: null, datafim: null,
      selectedDocs: s.tiposDeDocs, selectedFornec: s.tiposDeFornec,
      selectedEmb: s.tiposDeEmb, selectedReg: s.tiposDeReg,
      selectedCC: s.tiposDeCert,
    });
  };


  // recarrega todo o recordset aplicando as restriçoes de datas, se houver
  const updateDashData = async (isInitialLoad = false) => {
    updateState({ loadingDash: true });
    try {
      const s = stateRef.current;
      const params = {};
      if (s.dataini) params.dataIni = moment(s.dataini).format('DD/MM/YYYY');
      if (s.datafim) params.dataFim = moment(s.datafim).format('DD/MM/YYYY');
      
      const statsResp = await api.get('dashboard/stats', { params });

      // Helper local para normalizar a label
      const normalizeLabel = (v) => {
        if (!v) return null;
        const str = String(v).toLowerCase();
        return str.charAt(0).toUpperCase() + str.slice(1);
      };

      // ajusta os campos vazios/nao fornecidos
      const rawdcs = (statsResp.docs || []).map(d => ({
        doc_id: d.doc_id, status: d.status, dtaprox: d.dtaprox || 'Indeterminada',
        doctype: d.doctype || 'Sem tipo', supplier: d.supplier || 'Sem fornecedor',
        boat: d.boat || 'Sem embarcação', region: d.region || 'Indeterminado', aprov: d.aprov,
        rd_ctd: d.rd_ctd, temPendencia: !!d.tem_pendencia, editado: !!d.editado, pr_om: !!d.pr_om,
      }));

      // Deriva os certificados de calibração direto do rawdcs em JavaScript
      const rawcc = rawdcs
        .filter(d => d.doctype === 'Certificado de Calibração' && d.aprov)
        .map(d => {
          const aprovNorm = normalizeLabel(d.aprov);
          return {
            ...d,
            label: aprovNorm,
            value: aprovNorm
          };
        });

      // calcula os itens dos menus de selecao de cada dimensao
      const tiposDeDocs = _.uniq(rawdcs.map(x => x.doctype)).sort((a, b) => a.localeCompare(b, 'pt', { sensitivity: 'base' }));
      const tiposDeFornec = _.uniq(rawdcs.map(x => x.supplier)).sort((a, b) => a.localeCompare(b, 'pt', { sensitivity: 'base' }));
      const tiposDeEmb = _.uniq(rawdcs.map(x => x.boat)).sort((a, b) => a.localeCompare(b, 'pt', { sensitivity: 'base' }));
      const tiposDeReg = _.uniq(rawdcs.map(x => x.region)).sort((a, b) => a.localeCompare(b, 'pt', { sensitivity: 'base' }));
      const certMap = _.mapObject(_.groupBy(rawcc, 'label'), v => v.length);
      const certKeys = Object.keys(certMap).sort((a, b) => a.localeCompare(b, 'pt', { sensitivity: 'base' }));

      // Resgate das preferências salvas cruzando com dados reais
      const saved = s.savedMenuFilters || {};

      // Helper: Se existe filtro salvo, pega só o que a base retornou (interseção)
      // Se não existir filtro salvo ou se a interseção for vazia, marca "Todos"
      const getInitial = (currentScreenArr, savedDBArr, allArr, isCurrentlyAllSelected) => {
        
        // 1. SE FOR UM "ATUALIZAR" (não é o mount inicial)
        if (isInitialLoad !== true) {
          // Se a flag de "Todos" estava marcada, mantemos todos (assim novos itens que vieram do banco entram marcados)
          if (isCurrentlyAllSelected) return allArr;

          // Se a pessoa desmarcou itens (ou desmarcou tudo), currentScreenArr será preservado.
          if (Array.isArray(currentScreenArr)) {
            // Filtra só pra garantir que não fiquem selecionados itens "fantasmas" que sumiram do banco nesta atualização
            return currentScreenArr.filter(x => allArr.includes(x));
          }
          return allArr;
        }

        // 2. SE FOR A CARGA INICIAL (Lendo do Banco)
        if (savedDBArr && Array.isArray(savedDBArr)) {
          // Se o array salvo tiver itens, fazemos a interseção
          if (savedDBArr.length > 0) {
            const intersection = savedDBArr.filter(x => allArr.includes(x));
            return intersection.length > 0 ? intersection : allArr;
          }
          // Se a pessoa tiver salvo um painel vazio no banco antes de sair, respeitamos o vazio!
          return [];
        }
        
        return allArr;
      };

      const initDocs = getInitial(s.selectedDocs, saved.selectedDocs, tiposDeDocs, s.selectedAll.selectedDocs);
      const initFornec = getInitial(s.selectedFornec, saved.selectedFornec, tiposDeFornec, s.selectedAll.selectedFornec);
      const initEmb = getInitial(s.selectedEmb, saved.selectedEmb, tiposDeEmb, s.selectedAll.selectedEmb);
      const initReg = getInitial(s.selectedReg, saved.selectedReg, tiposDeReg, s.selectedAll.selectedReg);
      const initCC = getInitial(s.selectedCC, saved.selectedCC, certKeys, s.selectedAll.selectedCC);

      updateState({
        rawdcs, rawCerts: rawcc, 
        certs: getDataSet(certMap),
        selectedCC: initCC,       // <- Trocado de certKeys para initCC
        tiposDeCert: certKeys,
        tiposDeDocs, tiposDeFornec, tiposDeEmb, tiposDeReg
      });

      applyFilter({ 
        selectedDocs: initDocs,     // <- Trocado de tiposDeDocs para initDocs
        selectedFornec: initFornec, // <- Trocado de tiposDeFornec para initFornec
        selectedEmb: initEmb,       // <- Trocado de tiposDeEmb para initEmb
        selectedReg: initReg,       // <- Trocado de tiposDeReg para initReg
        selectedCC: initCC          // <- Trocado de tiposDeCert para initCC 
      });
      
    } finally {
      updateState({ loadingDash: false });
    }
  };


  // CARREGA USER PREFERENCES
  const getUserParametersDate = async () => {
    try {
      const userPrefs = await api.get(`/user/preferences?user_id=${user.id}`);
      
      // Extração e tipagem das Datas
      let dataini = null;
      let datafim = null;
      
      let dateParamsObj = userPrefs?.selectDateParameters;
      if (typeof dateParamsObj === 'string') {
        try { dateParamsObj = JSON.parse(dateParamsObj); } 
        catch { dateParamsObj = {}; }
      }

      if (dateParamsObj?.dataInicio && dateParamsObj.dataInicio.trim() !== "") {
        const iniMoment = moment(dateParamsObj.dataInicio, 'DD/MM/YYYY');
        if (iniMoment.isValid()) dataini = iniMoment.format('YYYY-MM-DDTHH:mm:ss.SSS');
      }
      if (dateParamsObj?.dataFim && dateParamsObj.dataFim.trim() !== "") {
        const fimMoment = moment(dateParamsObj.dataFim, 'DD/MM/YYYY');
        if (fimMoment.isValid()) datafim = fimMoment.format('YYYY-MM-DDTHH:mm:ss.SSS');
      }

      // Extração e tipagem do Gráfico
      let graphDataObj = userPrefs?.graphdata;
      if (typeof graphDataObj === 'string') {
        try { graphDataObj = JSON.parse(graphDataObj); } 
        catch { graphDataObj = {}; }
      }

      // Extração e tipagem dos Menus Salvos (NOVO)
      let savedFiltersObj = userPrefs?.selectedDash;
      if (typeof savedFiltersObj === 'string') {
        try { savedFiltersObj = JSON.parse(savedFiltersObj); } 
        catch { savedFiltersObj = null; }
      }

      // Atualiza estado convertendo dados salvos em booleanos se aplicável
      updateState({ 
        dataini, datafim,
        showUnread: Boolean(userPrefs?.unread) || false,
        showPend: Number(userPrefs?.pendencias) === 1,
        showEdt: Number(userPrefs?.edt) === 1,
        chartType: graphDataObj?.chartType ?? 'doughnut',
        sortType: graphDataObj?.sortType ?? 'alfa',
        sortOrd: graphDataObj?.sortOrd ?? 'asc',
        savedMenuFilters: savedFiltersObj,
      });

    } catch (e) { console.error("Error fetching User Dashboard Prefs", e); }
  };


  const handlePressSelectAll = (allItemsKey, selectedItemsKey) => {
    const s = stateRef.current;
    const allItems = _lodash.get(s, allItemsKey, []);
    const selectedItems = _lodash.get(s, selectedItemsKey, []);
    const isAllSelected = selectedItems.length === allItems.length && allItems.length > 0;
    applyFilter({ [selectedItemsKey]: isAllSelected ? [] : [...allItems] });
  };


  const handleOpenAnalyticModal = ({ title, label, }) => {
    const s = stateRef.current;
    
    // Função "trator" que normaliza qualquer coisa para uma string limpa e minúscula
    const safeString = (val) => String(val || '').trim().toLowerCase();
    
    // Normaliza a label que veio do clique no gráfico
    const safeLabel = safeString(label);

    // Filtra também os processados para que a tabela bata com o valor do gráfico
    const rawProcessadosModal = (s.rawdcs || []).filter(d => String(d.status).trim().toLowerCase() === 'processado');
    // Pega a lista que já passou pelos seus filtros do topo da tela (não lidos, fornecedores, etc)
    const fd = filterDocs(rawProcessadosModal || [], {
      showUnread: s.showUnread,
      showPend: s.showPend,
      showEdt: s.showEdt,
      selectedDocs: s.selectedDocs,
      selectedFornec: s.selectedFornec,
      selectedEmb: s.selectedEmb,
      selectedReg: s.selectedReg
    });

    let filteredItems = [];

    // Comparações blindadas contra tipos, espaços e casing
    if (title === DASH_TITLES.TIPO) { filteredItems = fd.filter(d => safeString(d.doctype) === safeLabel); } 
    else if (title === DASH_TITLES.FORNECEDOR) { filteredItems = fd.filter(d => safeString(d.supplier) === safeLabel); } 
    else if (title === DASH_TITLES.EMBARCACAO) { filteredItems = fd.filter(d => safeString(d.boat) === safeLabel); } 
    else if (title === DASH_TITLES.REGIAO) { filteredItems = fd.filter(d => safeString(d.region) === safeLabel); } 
    else if (title === DASH_TITLES.CERTIFICADOS) { filteredItems = (s.rawCerts || []).filter(c => safeString(c.label) === safeLabel); }

    updateState({
      showAnalyticModal: true,
      analyticData: { title, label, items: filteredItems }
    });
  };

  const exportTableStatsToExcel = ({ title, dateRangeText, column, labels, values, showModalKey }) => {
    if (!labels?.length || !values?.length) return;

    const finalTitle = title ? `Dashboard - ${title}` : 'Dashboard';
    const total = values.reduce((acc, v) => acc + v, 0);

    // 1. Mapeamento de Dados (incluindo a linha de Total no final)
    const data = labels.map((label, idx) => [
      label,
      values[idx],
      values[idx] / total
    ]);
    
    // Adicionamos o rodapé com os totais ao final dos dados
    data.push(['Total', total, 1]);

    // Chamada da Função Genérica de geração da planilha
    exportStandardExcel({
      title: finalTitle,
      filtersText: dateRangeText || '', // Garante que não quebre se for undefined
      columns: [
        { header: column, width: 21 },
        { header: 'Quantidade', width: 10.5 },
        { header: '%', width: 9 }
      ],
      data: data,
      fileNamePrefix: title || 'Dashboard',
      // Formatação Customizada (Números e Porcentagens)
      customFormatting: (worksheet, mappedData) => {
        mappedData.forEach((_, idx) => {
          const row = 5 + idx; // Os dados começam na linha 5
          
          // Formatação da Coluna B (Quantidade)
          if (worksheet[`B${row}`]) {
            worksheet[`B${row}`].t = 'n'; // Tipo numérico
            worksheet[`B${row}`].z = '#,##0'; // Formato de milhar
          }
          
          // Formatação da Coluna C (Porcentagem)
          if (worksheet[`C${row}`]) {
            worksheet[`C${row}`].t = 'n'; // Tipo numérico
            worksheet[`C${row}`].z = '0.00%'; // Formato de porcentagem
          }
        });
      }
    });

    // Ação de fechamento do modal
    if (showModalKey) updateState({ [showModalKey]: false });
  };


  // =========================================================================
  // EFFECTS - CICLO DE VIDA DO COMPONENTE
  // =========================================================================

  // MOUNT
  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;

    const handleResize = () => updateState({ windowWidth: window.innerWidth });
    window.addEventListener("resize", handleResize);

    const init = async () => {
      await getUserParametersDate();
      await updateDashData(true);

      const s = stateRef.current;
      prevDeps.current = {
        dataini: s.dataini, datafim: s.datafim, 
        chartType: s.chartType, sortType: s.sortType, sortOrd: s.sortOrd, 
        showUnread: s.showUnread, showPend: s.showPend, showEdt: s.showEdt,
        selectedDocs: s.selectedDocs, selectedFornec: s.selectedFornec, 
        selectedEmb: s.selectedEmb, selectedReg: s.selectedReg, selectedCC: s.selectedCC
      };
      isReady.current = true;
    };
    init();

    return () => {
      window.removeEventListener("resize", handleResize);
      // Limpa qualquer salvamento pendente se o componente for destruído
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (UPDATE)
  useEffect(() => {
    if (!isReady.current) return;

    let getbody = false;
    let updateGraph = false;
    let shouldSavePrefs = false;
    
    const p = prevDeps.current;
    const s = state;

    // Mudança nas Datas
    if (p.dataini !== s.dataini || p.datafim !== s.datafim) {
      getbody = true;
      shouldSavePrefs = true;
    }

    // Mudança Preferencias de Gráfico
    if (p.chartType !== s.chartType || p.sortType !== s.sortType || p.sortOrd !== s.sortOrd) {
      shouldSavePrefs = true;
      if (p.sortType !== s.sortType || p.sortOrd !== s.sortOrd) updateGraph = true;
    }

    // Mudança nas chaves globais (não lidos, pendências, editados)
    if (p.showUnread !== s.showUnread || p.showPend !== s.showPend || p.showEdt !== s.showEdt) {
      shouldSavePrefs = true;
    }

    // Mudança nas seleções dos menus
    const menusChanged = 
      p.selectedDocs !== s.selectedDocs || 
      p.selectedFornec !== s.selectedFornec || 
      p.selectedEmb !== s.selectedEmb || 
      p.selectedReg !== s.selectedReg || 
      p.selectedCC !== s.selectedCC;
    if (menusChanged) { shouldSavePrefs = true; }

    // Dispara apenas um post consolidado caso haja mudanças
    if (shouldSavePrefs) {
      // Se já tinha um envio agendado, cancela ele (o usuário continuou clicando)
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      
      // Agenda um novo envio para daqui a 1 segundo (1000 ms)
      saveTimerRef.current = setTimeout(() => { saveUserPreferences(s); }, 1000); 
    }

    // Ações na Interface
    if (getbody) { updateDashData(false); } 
    else if (updateGraph) {
      updateState({
        docs: getDataSet(state.aggDocs), 
        suppliers: getDataSet(state.aggSuppliers),
        boats: getDataSet(state.aggBoats), 
        region: getDataSet(state.aggReg),
        certs: getDataSet(state.aggCerts),
      });
    }

    prevDeps.current = {
      dataini: s.dataini, datafim: s.datafim, 
      chartType: s.chartType, sortType: s.sortType, sortOrd: s.sortOrd, 
      showUnread: s.showUnread, showPend: s.showPend, showEdt: s.showEdt,
      selectedDocs: s.selectedDocs, selectedFornec: s.selectedFornec, 
      selectedEmb: s.selectedEmb, selectedReg: s.selectedReg, selectedCC: s.selectedCC
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.dataini, state.datafim, state.chartType, state.sortType, state.sortOrd, 
      state.showUnread, state.showPend, state.showEdt,
      state.selectedDocs, state.selectedFornec, state.selectedEmb, state.selectedReg, state.selectedCC,
  ]);


  // ====================================================
  // === RENDERS E COMPONENTES INTERNOS DE APOIO ===
  // ====================================================

  // renderiza o modal da tabela sumario
  const renderTableModal = ({ title, state, column, labels = [], values = [], showModalKey }) => {
    const s = state;
    const open = _lodash.get(s, showModalKey, false);
    const total = values.reduce((acc, val) => acc + val, 0);

    const parts = [];
    if (s.showUnread) parts.push('Não lidos');
    if (s.showPend) parts.push('Com pendências');
    if (s.showEdt) parts.push('Editados');
    if (s.dataini && s.datafim) parts.push(`Entre ${moment(s.dataini).format('DD/MM/YYYY')} e ${moment(s.datafim).format('DD/MM/YYYY')}`);
    else if (s.dataini) parts.push(`Desde ${moment(s.dataini).format('DD/MM/YYYY')}`);
    else if (s.datafim) parts.push(`Até ${moment(s.datafim).format('DD/MM/YYYY')}`);
    
    const dateRangeText = parts.length ? parts.join(', ') : '';

    return (
      <Dialog open={open} onClose={() => updateState({ [showModalKey]: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ lineHeight: 1.2 }}>{title}</span>
              {dateRangeText && <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>{dateRangeText}</span>}
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Exportar para Excel" arrow>
                <IconButton 
                  size="large" 
                  sx={{ marginLeft: '0.3rem', color: '#106436ff' }} 
                  onClick={() => exportTableStatsToExcel({ title, dateRangeText, column, labels, values, showModalKey })}>
                  <TableChartOutlined />
                </IconButton>
              </Tooltip>

              <Tooltip title="Fechar" arrow>
                <IconButton size="large" onClick={() => updateState({ [showModalKey]: false })}><CloseIcon /></IconButton>
              </Tooltip>
            </div>
          </div>
        </DialogTitle>

        <DialogContent>
          <Table size="small" sx={{ cursor: 'text', userSelect: 'text' }}>
            <TableHead>
              <TableRow>
                <TableCell><b>{column}</b></TableCell>
                <TableCell align="right"><b>Quantidade</b></TableCell>
                <TableCell align="right"><b>%</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {labels.map((label, index) => {
                const percent = total > 0 ? (values[index] / total) * 100 : 0;
                return (
                  // 1. Tooltip envolvendo a linha, com followCursor para o balãozinho acompanhar o mouse
                  <Tooltip key={index} title="Analítico" arrow placement="top" followCursor>
                    <TableRow 
                      hover
                      style={{ cursor: 'pointer' }} // 2. Cursor de "mãozinha" ativado
                      onClick={() => {
                        // updateState({ [showModalKey]: false }); // Opcional: Fecha a modal atual de totalizadores para não empilhar

                        // Se o usuário selecionou algum texto, aborta o clique para não abrir a modal
                        const selection = window.getSelection().toString();
                        if (selection.length > 0) {
                          return; 
                        }
                        // senão, abre a modal analítica passando a fatia exata clicada
                        handleOpenAnalyticModal({
                          title: title,
                          label: label,
                        });
                      }}
                    >
                      <TableCell>{label}</TableCell>
                      <TableCell align="right">{new Intl.NumberFormat('pt-BR').format(values[index])}</TableCell>
                      <TableCell align="right">{percent.toFixed(2)}%</TableCell>
                    </TableRow>
                  </Tooltip>
                );
              })}
              
              {/* Linha de TOTAL sem clique */}
              <TableRow>
                <TableCell><b>Total</b></TableCell>
                <TableCell align="right"><b>{new Intl.NumberFormat('pt-BR').format(total)}</b></TableCell>
                <TableCell align="right"><b>100.00%</b></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    );
  };

  // MONTA UM CONJUNTO COMPLETO DE CANVAS DE GRAFICO
  const makeFilterChart = ({ title, state, allItemsKey, selectedItemsKey, selectedAllFlagKey, chartDataKey, showTableKey, sizeGraph = '100%' }) => {
    const s = state;
    const menuAnchorKey = allItemsKey + "Menu";
    const menuAnchor = _lodash.get(s, menuAnchorKey);
    const allItems = _lodash.get(s, allItemsKey, []);
    const selectedItems = _lodash.get(s, selectedItemsKey, []);
    const selectedAllFlag = _lodash.get(s, selectedAllFlagKey, false);
    const chartData = _lodash.get(s, `${chartDataKey}.chart`, { labels: [], datasets: [] });
    const tableValues = _lodash.get(s, `${chartDataKey}.table.values`, []);
    const totalDocs = tableValues?.reduce((acc, curr) => acc + (Number(curr) || 0), 0).toLocaleString('pt-BR') || 0;
    // PEGA AS LABELS ORIGINAIS (SEM O " = VALOR")
    const tableLabels = _lodash.get(s, `${chartDataKey}.table.labels`, []);

    return (
      <D className="col e aF" style={{ margin: '0px', padding: '0 1rem', borderRadius: '1rem', backgroundColor: '#fff' }}>
        <D style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '0.5rem' }}>
          {/* BOTÃO DE SELECAO DE ITENS */}
          <Tooltip title={`Seleção de ${title}`} arrow>
            <Button 
              style={{ flexGrow: 1, marginRight: '1rem' }} 
              color={selectedAllFlag ? 'info' : 'primary'}
              // variant="outlined" 
              variant={selectedAllFlag ? 'outlined' : 'contained'}
              onClick={e => updateState({ [menuAnchorKey]: e.currentTarget })} 
            >
              {title} ({totalDocs})
            </Button>
          </Tooltip>
          {/* BOTAO DE ACIONAMENTO DA TABELA DE CONTADORES DO GRAFICO */}
          {!!tableValues.length && (
            <Tooltip title={`Tabela de ${title}`} arrow>
              <IconButton size="large" onClick={() => updateState({ [showTableKey]: true })}><TableView fontSize="large" /></IconButton>
            </Tooltip>
          )}
        </D>

        {/* MENU DE ITENS DISPONIVEIS NO GRAFICO */}
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => updateState({ [menuAnchorKey]: null })}>
          <D className="col e aF scD scB hv" style={{ maxHeight: '30vh', padding: '0.3rem 1rem 0.3rem 0.3rem' }}>
            <D className="row aC" onClick={() => handlePressSelectAll(allItemsKey, selectedItemsKey)}>
              <Checkbox checked={selectedAllFlag} />
              <D e>Todos</D>
            </D>
            {allItems.map(a => (
              <D className="row aC" key={a} onClick={() => {
                const sel = _lodash.get(stateRef.current, selectedItemsKey, []);
                applyFilter({ [selectedItemsKey]: sel.includes(a) ? _lodash.without(sel, a) : _lodash.union(sel, [a]) });
              }}>
                <Checkbox checked={selectedItems.includes(a)} />
                <D e>{a}</D>
              </D>
            ))}
          </D>
        </Menu>

        {/* ESPAÇO DO GRAFICO  */}
        <DashboardGraph 
          title={title}
          chartType={s.chartType} 
          data={chartData || []} 
          originalLabels={tableLabels}  // labels originais
          widthContainer={sizeGraph} 
          aspectRatio={1.7}
          onElementClick={handleOpenAnalyticModal}
        />
      </D>
    );
  };

  const sizeGraph = state.windowWidth > 1400 ? '95%' : '100%';

  const toggleSx = (type) => ({
    '&.Mui-selected': { backgroundColor: 'transparent' },
    '& .MuiSvgIcon-root': { color: state.chartType === type ? 'primary.main' : 'text.secondary' },
    '&:hover': { '& .MuiSvgIcon-root': { color: state.chartType === type ? 'primary.main' : 'text.primary' } },
  });

  const toggleSxSort = (type) => ({
    '&.Mui-selected': { backgroundColor: 'transparent' },
    '& .MuiSvgIcon-root': { color: state.sortType === type ? 'primary.main' : 'text.secondary' },
    '&:hover': { '& .MuiSvgIcon-root': { color: state.sortType === type ? 'primary.main' : 'text.primary' } },
  });

  const toggleSxOrd = (type) => ({
    '&.Mui-selected': { backgroundColor: 'transparent' },
    '& .MuiSvgIcon-root': { color: state.sortOrd === type ? 'primary.main' : 'text.secondary' },
    '&:hover': { '& .MuiSvgIcon-root': { color: state.sortOrd === type ? 'primary.main' : 'text.primary' } },
  });

  const computeHasFilters = (currentState = state) => {
    const {
      dataini, datafim, showUnread, showPend, showEdt, selectedAll,
    } = currentState;

    // Verifica se algum dos menus não está com a seleção "Todos"
    const hasPartialMenuSelection = 
      !selectedAll.selectedDocs || 
      !selectedAll.selectedFornec || 
      !selectedAll.selectedEmb || 
      !selectedAll.selectedReg || 
      !selectedAll.selectedCC;

    return (
      !!dataini || !!datafim ||
      showUnread || showPend || showEdt ||
      hasPartialMenuSelection
    );
  };
  const temFiltro = computeHasFilters();

  return (
    <D className="col e aF scD" style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
      
      {/* INDICADOR DE CARGA DE DADOS */}
      {state.loadingDash && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'all' }}>
          <CircularProgress size={48} />
        </div>
      )}

      {/* == 1a LINHA = ÁREA DOS FILTROS E CONTADORES == */}
      <div style={{ position: 'sticky', top: 85, width: '100%', backgroundColor: '#fff', zIndex: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'center', alignContent: 'center', justifyContent: 'space-between', padding: '0 1rem', minHeight: '35px', rowGap: '6px' }}>
        
        {/* 1o conjunto alinhado à esquerda */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', flex: '1 1 auto', minWidth: 0 }}>
          
          {/* === BLOCO DAS DATAS (Box removido, estilos unificados no Stack e marginRight removido) */}
          <Stack direction="row" sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#eeeeee', borderRadius: '0.5rem', padding: '0 4px', height: '40px' }}>
            
            {/* data inicial */}
            <MuiTooltip title={'Data inicial ' + (!!state.dataini ? moment(state.dataini).format('DD/MM/YYYY') : '')} arrow>
              <IconButton onClick={e => updateState({ aproxIniDateMenu: e.currentTarget })} color={!!state.dataini ? 'primary' : 'default'}>
                <DateRangeOutlined />
              </IconButton>
            </MuiTooltip>
            <Menu 
              anchorEl={state.aproxIniDateMenu} 
              open={!!state.aproxIniDateMenu} 
              onClose={() => updateState({ aproxIniDateMenu: null })}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <StaticDatePicker 
                  displayStaticWrapperAs="desktop" 
                  value={state.dataini} 
                  maxDate={state.datafim || undefined} 
                  onChange={v => updateState({ dataini: v, aproxIniDateMenu: null })} 
                  renderInput={params => <TextField {...params} />} />
              </LocalizationProvider>
              <DialogActions>
                <Button variant="outlined" color="primary" onClick={() => updateState((prev) => {
                    return { ...prev, dataini: "", aproxIniDateMenu: null };
                  })}>
                  Limpar data
                </Button>
              </DialogActions>
            </Menu>

            {/* data final */}
            <MuiTooltip title={'Data final ' + (!!state.datafim ? moment(state.datafim).format('DD/MM/YYYY') : '')} arrow>
              <IconButton onClick={e => updateState({ aproxFimDateMenu: e.currentTarget })} color={!!state.datafim ? 'primary' : 'default'}>
                <DateRangeOutlined />
              </IconButton>
            </MuiTooltip>
            <Menu anchorEl={state.aproxFimDateMenu} open={!!state.aproxFimDateMenu} onClose={() => updateState({ aproxFimDateMenu: null })}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <StaticDatePicker 
                  displayStaticWrapperAs="desktop" 
                  value={state.datafim} 
                  minDate={state.dataini || undefined} 
                  onChange={v => updateState({ datafim: v, aproxFimDateMenu: null })} 
                  renderInput={params => <TextField {...params} />} />
              </LocalizationProvider>
              <DialogActions>
                <Button variant="outlined" color="primary" onClick={() => updateState((prev) => {
                    return { ...prev, datafim: "", aproxFimDateMenu: null };
                  })}>
                  Limpar data
                </Button>
              </DialogActions>
            </Menu>

          </Stack>

          {/* === BLOCO DOS SWITCHES/FILTROS */}
          {/* nao lidos */}
          <Stack direction="row" sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#eeeeee', borderRadius: '0.5rem', padding: '0 8px', height: '40px', whiteSpace: 'nowrap' }}>
            <Typography variant="body2">Todos</Typography>
            <Switch checked={state.showUnread} onChange={e => {
              updateState({ showUnread: e.target.checked })
              applyFilter(); // Atualiza os gráficos no mesmo ciclo!
            }} />
            <Typography variant="body2">Não lidos</Typography>
          </Stack>

          {/* editados */}
          <Stack direction="row" sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#eeeeee', borderRadius: '0.5rem', padding: '0 8px', marginLeft: '8px', height: '40px', whiteSpace: 'nowrap' }}>
            <Typography variant="body2">Todos</Typography>
            <Switch checked={state.showEdt} onChange={e => {
              updateState({ showEdt: e.target.checked })
              applyFilter(); // Atualiza os gráficos no mesmo ciclo!
            }} />
            <Typography variant="body2">Editados</Typography>
          </Stack>

          {/* com pendencias */}
          <Stack direction="row" sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#eeeeee', borderRadius: '0.5rem', padding: '0 8px', marginLeft: '8px', height: '40px', whiteSpace: 'nowrap' }}>
            <Typography variant="body2">Todos</Typography>
            <Switch checked={state.showPend} onChange={e => {
              updateState({ showPend: e.target.checked })
              applyFilter(); // Atualiza os gráficos no mesmo ciclo!}
            }} />
            <Typography variant="body2">C/ pendências</Typography>
          </Stack>
          
          {/* === BOTOES DE AÇAO */}
          {temFiltro && (
            <Tooltip title="Limpar filtros" arrow><IconButton onClick={cleanFilter} ><FilterAltOffIcon color="action" /></IconButton></Tooltip>
          )}

          <Tooltip title="Atualizar" arrow><IconButton onClick={updateDashData} color="default"><Refresh /></IconButton></Tooltip>
        </div>

        {/* 2o conjunto: alinhado à direita */}
        {/* === BLOCO DOS CONTADORES  */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.7rem', alignItems: 'center', justifyContent: 'flex-end', flex: '0 0 auto' }}>
          {/* <DashboardCard title="Em Processamento" value={new Intl.NumberFormat('pt-BR').format(state.stats.onproc)} color="#1976D2" icon={<Sync sx={{ fontSize: 28, color: '#fff' }} />} /> */}
          <DashboardCard title="Com Erro" value={new Intl.NumberFormat('pt-BR').format(state.stats.procerr)} color="#fc1e4a" icon={<DangerousOutlined sx={{ fontSize: 28, color: '#fff' }} />} />
          <DashboardCard title="Processados" value={new Intl.NumberFormat('pt-BR').format(state.stats.proc)} color="#2E7D32" icon={<CheckCircleOutline sx={{ fontSize: 28, color: '#fff' }} />} />
          <DashboardCard title="Não Lidos" value={new Intl.NumberFormat('pt-BR').format(state.stats.unread)} color="#efc002ff" icon={<VisibilityOffIcon sx={{ fontSize: 28, color: '#fff' }} />} />
          <DashboardCard title="Editados" value={new Intl.NumberFormat('pt-BR').format(state.stats.edt)} color="rgb(239, 136, 2)" icon={<Edit sx={{ fontSize: 28, color: '#fff' }} />} />
          <DashboardCard title="C/ Pendências" value={new Intl.NumberFormat('pt-BR').format(state.stats.pend)} color="rgb(239, 116, 2)" icon={<PendingActions sx={{ fontSize: 28, color: '#fff' }} />} />
        </div>
      </div>

      {/* === 2A LINHA: BOTOES DE SELEÇÃO DOS GRAFICOS == */}
      <div style={{ display: 'flex',  alignItems: 'center', justifyContent: 'center', padding: '2rem 0 0.5rem 1rem', flexShrink: 0 }}>
        <ToggleButtonGroup value={state.chartType} exclusive size="small" onChange={(e, value) => value && updateState({ chartType: value })}>
          <Tooltip title="Gráfico Doughnut" arrow><ToggleButton value="doughnut" sx={toggleSx('doughnut')}><DonutLargeIcon fontSize="medium" /></ToggleButton></Tooltip>
          <Tooltip title="Gráfico Pizza" arrow><ToggleButton value="pie" sx={toggleSx('pie')}><PieChartIcon fontSize="medium" /></ToggleButton></Tooltip>
          <Tooltip title="Gráfico de Barras" arrow><ToggleButton value="bar" sx={toggleSx('bar')}><BarChartIcon fontSize="medium" /></ToggleButton></Tooltip>
        </ToggleButtonGroup>

        <ToggleButtonGroup value={state.sortType} exclusive sx={{ marginLeft: '16px' }} size="small" onChange={(e, value) => value && updateState({ sortType: value })}>
          <Tooltip title="Ordenação Alfabética" arrow><ToggleButton value="alfa" sx={toggleSxSort('alfa')}><SortByAlphaIcon fontSize="medium" /></ToggleButton></Tooltip>
          <Tooltip title="Ordenação Numérica" arrow><ToggleButton value="num" sx={toggleSxSort('num')}><PinOutlinedIcon fontSize="medium" /></ToggleButton></Tooltip>
        </ToggleButtonGroup>

        <ToggleButtonGroup value={state.sortOrd} exclusive sx={{ marginLeft: '16px' }} size="small" onChange={(e, value) => value && updateState({ sortOrd: value })}>
          <Tooltip title="Ascendente" arrow><ToggleButton value="asc" sx={toggleSxOrd('asc')}><ArrowUpwardIcon fontSize="medium" /></ToggleButton></Tooltip>
          <Tooltip title="Descendente" arrow><ToggleButton value="desc" sx={toggleSxOrd('desc')}><ArrowDownwardIcon fontSize="medium" /></ToggleButton></Tooltip>
        </ToggleButtonGroup>
      </div>

      {/* === 3A LINHA: RENDERIZAÇÃO DOS GRÁFICOS == */}
      <div className="col w aF" style={{ flex: 1, overflowY: 'auto', padding: 0 }}>
        {makeFilterChart({ title: DASH_TITLES.TIPO, state, allItemsKey: "tiposDeDocs", selectedItemsKey: "selectedDocs", selectedAllFlagKey: "selectedAll.selectedDocs", showTableKey: "showDocsTable", chartDataKey: "docs", sizeGraph })}
        {makeFilterChart({ title: DASH_TITLES.FORNECEDOR, state, allItemsKey: "tiposDeFornec", selectedItemsKey: "selectedFornec", selectedAllFlagKey: "selectedAll.selectedFornec", showTableKey: "showSuppliersTable", chartDataKey: "suppliers", sizeGraph })}
        {makeFilterChart({ title: DASH_TITLES.REGIAO, state, allItemsKey: "tiposDeReg", selectedItemsKey: "selectedReg", selectedAllFlagKey: "selectedAll.selectedReg", showTableKey: "showRegTable", chartDataKey: "region", sizeGraph })}
        {makeFilterChart({ title: DASH_TITLES.EMBARCACAO, state, allItemsKey: "tiposDeEmb", selectedItemsKey: "selectedEmb", selectedAllFlagKey: "selectedAll.selectedEmb", showTableKey: "showBoatsTable", chartDataKey: "boats", sizeGraph })}
        {makeFilterChart({ title: DASH_TITLES.CERTIFICADOS, state, allItemsKey: "tiposDeCert", selectedItemsKey: "selectedCC", selectedAllFlagKey: "selectedAll.selectedCC", showTableKey: "showCertsTable", chartDataKey: "certs", sizeGraph })}
      </div>

      {/* === MODAIS === */}
      {/* === Tabelas dos totais */}
      {renderTableModal({ title: DASH_TITLES.TIPO, state, column: "Tipo de documento", labels: state.docs.table.labels, values: state.docs.table.values, showModalKey: "showDocsTable" })}
      {renderTableModal({ title: DASH_TITLES.FORNECEDOR, state, column: "Fornecedor", labels: state.suppliers.table.labels, values: state.suppliers.table.values, showModalKey: "showSuppliersTable" })}
      {renderTableModal({ title: DASH_TITLES.REGIAO, state, column: C.groupLabel, labels: state.region.table.labels, values: state.region.table.values, showModalKey: "showRegTable" })}
      {renderTableModal({ title: DASH_TITLES.EMBARCACAO, state, column: "Embarcação", labels: state.boats.table.labels, values: state.boats.table.values, showModalKey: "showBoatsTable" })}
      {renderTableModal({ title: DASH_TITLES.CERTIFICADOS, state, column: "Status", labels: state.certs.table.labels, values: state.certs.table.values, showModalKey: "showCertsTable" })}
      
      {/* === ANALITICO */}
      {/* montagem da string de filtros  */}
      {(() => {
        const parts = [];
        // Filtros de Data
        if (state.dataini && state.datafim) {
          parts.push(`De ${moment(state.dataini).format('DD/MM/YYYY')} até ${moment(state.datafim).format('DD/MM/YYYY')}`);
        } else if (state.dataini) {
          parts.push(`A partir de ${moment(state.dataini).format('DD/MM/YYYY')}`);
        } else if (state.datafim) {
          parts.push(`Até ${moment(state.datafim).format('DD/MM/YYYY')}`);
        }
        
        // Filtros de Status
        if (state.showUnread) parts.push('Não lidos');
        if (state.showPend) parts.push('Com pendências');
        if (state.showEdt) parts.push('Editados');

        // Se tiver filtros ativos monta a string
        const filterSubtitle = parts.length > 0 ? `${parts.join(', ')}` : '';

        return (
          <AnalyticModal 
            key={`${state.analyticData?.title}-${state.analyticData?.label}`} //chave unica para resetar qdo mudar o modal
            open={state.showAnalyticModal}
            onClose={() => updateState({ showAnalyticModal: false })}
            title={state.analyticData.title}
            label={state.analyticData.label}
            items={state.analyticData.items}
            filterText={filterSubtitle}
            api={api}
          />
        );
      })
      ()}
    </D>
  );
};
